import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { WarningCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const GATE_EXEMPT_ROUTES = ["/onboarding", "/orientation", "/profile", "/guidelines", "/training", "/policies", "/missed-punch", "/call-log", "/meetings"];

export const ComplianceGate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("compliance-dismissed") === "1");

  useEffect(() => {
    if (user && user.role !== "admin") {
      api.get("/compliance/me")
        .then((r) => setStatus(r.data))
        .catch(() => setStatus({ outstanding: 0, blocked: false, items: [] }));
    }
  }, [user, pathname]);

  if (!user || user.role === "admin" || !status || status.outstanding === 0 || dismissed) return null;
  if (GATE_EXEMPT_ROUTES.some((r) => pathname.startsWith(r))) return null;

  const outstanding = status.items.filter((i) => i.status === "outstanding");
  const blocks = outstanding.filter((i) => i.effective_severity === "block");
  const reminders = outstanding.filter((i) => i.effective_severity === "reminder");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="compliance-gate">
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900" data-testid="compliance-gate-title">
          <WarningCircle size={22} className={status.blocked ? "text-[#C0392B]" : "text-[#B7791F]"} />
          {status.blocked ? "Action required before you can work" : "Outstanding items on your record"}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-stone-600">
          {status.blocked
            ? "These items must be completed before you can open client records. You can still complete onboarding, update your profile, and finish training using the buttons below — or contact the office."
            : "Please take care of these — this appears each time you log in until they're complete."}
        </p>
        {blocks.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="compliance-blocks">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#C0392B]">Blocking</p>
            {blocks.map((i) => (
              <div key={i.key} className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-3" data-testid={`compliance-item-${i.key}`}>
                <p className="text-sm font-medium text-[#C0392B]">{i.label}</p>
                {i.detail && <p className="mt-0.5 text-xs text-stone-600">{i.detail}</p>}
              </div>
            ))}
          </div>
        )}
        {reminders.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="compliance-reminders">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#8a5d14]">Reminders</p>
            {reminders.map((i) => (
              <div key={i.key} className="rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-3" data-testid={`compliance-item-${i.key}`}>
                <p className="text-sm font-medium text-[#8a5d14]">{i.label}</p>
                {i.detail && <p className="mt-0.5 text-xs text-stone-600">{i.detail}</p>}
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => navigate("/onboarding")} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="compliance-goto-onboarding">
            Go to Onboarding
          </Button>
          <Button onClick={() => navigate("/orientation")} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="compliance-goto-orientation">
            Go to Orientation
          </Button>
          <Button onClick={() => navigate("/profile")} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="compliance-goto-profile">
            Go to My Profile
          </Button>
          <Button onClick={() => navigate("/guidelines")} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="compliance-goto-training">
            Go to Training
          </Button>
          {!status.blocked && (
            <Button onClick={() => { sessionStorage.setItem("compliance-dismissed", "1"); setDismissed(true); }}
              variant="outline" className="border-stone-300 text-stone-500" data-testid="compliance-continue">
              Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
